import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaFacebook, FaTwitter, FaInstagram, FaYoutube, FaLinkedin, FaHeart, FaMapMarkerAlt, FaPhone, FaEnvelope, FaGlobe } from 'react-icons/fa';

const Footer: React.FC = () => {
  const footerLinks = {
    About: [
      { name: 'Our Mission', path: '/about/mission' },
      { name: 'Our Team', path: '/about/team' },
      { name: 'Annual Reports', path: '/about/reports' },
    ],
    Programs: [
      { name: 'Education', path: '/programs/education' },
      { name: 'Healthcare', path: '/programs/healthcare' },
      { name: 'Empowerment', path: '/programs/women' },
    ],
    Resources: [
      { name: 'Activities', path: '/activities' },
      { name: 'Events', path: '/events' },
      { name: 'Gallery', path: '/gallery' },
    ],
    'Get Involved': [
      { name: 'Donate', path: '/donate' },
      { name: 'Volunteer', path: '/volunteer' },
      { name: 'Partner', path: '/partnership' },
    ],
  };

  const socialLinks = [
    { icon: <FaYoutube />, href: 'https://www.youtube.com/@AlhajiInusahFoundation', label: 'YouTube', color: 'hover:bg-red-600' },
    { icon: <FaFacebook />, href: '#', label: 'Facebook', color: 'hover:bg-blue-600' },
    { icon: <FaTwitter />, href: '#', label: 'Twitter', color: 'hover:bg-sky-500' },
    { icon: <FaInstagram />, href: '#', label: 'Instagram', color: 'hover:bg-pink-600' },
  ];

  // Foundation headquarters coordinates (Obuasi, Ghana - adjust as needed)
  const headquartersAddress = "Alhaji Inusah Foundation Headquarters, Zongo - Obuasi West Municipal, Ghana";
  const mapSrc = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3775.368845938662!2d-1.6745169252690217!3d6.202704326808662!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xfdc6f004dac8e71%3A0xaf1dea791202c586!2sAlhaji%20Inusah%20Foundation%20Community%20Centre!5e1!3m2!1sen!2sgh!4v1766765116361!5m2!1sen!2sgh";

  const navigate = useNavigate();

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-slate-900 to-gray-950 text-white">
      {/* Top Section with Map */}
      <div className="px-4 py-6 md:py-8 border-b border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Map Container */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <FaMapMarkerAlt className="text-red-500" />
                <h3 className="text-lg font-bold text-white">Our Headquarters</h3>
              </div>
              
              <div className="rounded-xl overflow-hidden shadow-2xl border border-gray-700">
                <div className="h-48 md:h-56 w-full">
                  <iframe
                    src={mapSrc}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Alhaji Inusah Foundation Headquarters Location"
                    className="grayscale hover:grayscale-0 transition-all duration-500"
                  ></iframe>
                </div>
                
                <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-red-500/20 rounded-lg">
                      <FaMapMarkerAlt className="text-red-400 text-lg" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{headquartersAddress}</p>
                      <p className="text-gray-400 text-sm mt-1">Open: Monday - Friday, 9:00 AM - 6:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-white mb-4">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-2.5 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <FaPhone className="text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Phone</p>
                      <p className="font-medium">+233 549 870 386</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-2.5 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                    <div className="p-2 bg-green-500/20 rounded-lg">
                      <FaEnvelope className="text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Email</p>
                      <p className="font-medium">aifccobuasi@gmail.com</p>
                    </div>
                  </div>
                  
               
                </div>
              </div>
              
        
                {/* Social Media */}
              <div className="mb-4">
                <div className="flex items-center gap-3">
                  <p className="text-sm font-medium text-white">Follow Us</p>
                  {socialLinks.map((social) => (
                    <motion.a
                      key={social.label}
                      href={social.href}
                      className={`w-9 h-9 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center text-white transition-all duration-200 ${social.color} hover:scale-105`}
                      aria-label={social.label}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </div>
               <div className="flex gap-4">
                  <button onClick={() => navigate('/privacy')} className="text-cyan-100 hover:text-white text-sm">
                    Privacy Policy
                  </button>
                  <button onClick={() => navigate('/terms')} className="text-cyan-100 hover:text-white text-sm">
                    Terms of Service
                  </button>
                
                
                </div>
                  <div className="flex items-center gap-2 text-cyan-100 text-sm">
                  <span>Powered by</span>
                  <span className="px-2 py-1 bg-gradient-to-r from-cyan-500/30 to-blue-500/30 rounded text-cyan-300 text-xs font-semibold tracking-wide hover:from-cyan-500 hover:to-blue-500 hover:text-white transition-all duration-300">
                    AchiesTech
                  </span>
                </div>
                <p className="text-cyan-100 text-sm">
                  © {new Date().getFullYear()} Alhaji Inusah Foundation. All rights reserved.
                </p>
            </div>

        
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
